import * as React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../state/auth-state';
import { Spinner } from '../components/ui/spinner';

/**
 * Authenticated layout guard.
 * Redirects unauthenticated users to /login (saving the intended URL).
 * Provides the app shell with sticky header and main content area.
 * Per FRONTEND_CONSOLE_GUIDE.md: AuthenticatedLayout wraps protected routes.
 */
export function AuthenticatedLayout(): React.JSX.Element {
  const { state, logout } = useAuth();
  const location = useLocation();

  // Show loading spinner during session restoration
  if (state.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" aria-busy="true">
        <Spinner label="Restoring session..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!state.isAuthenticated) {
    sessionStorage.setItem('ddns_return_url', location.pathname);
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-foreground">DelegatedDNS</h1>
            <nav aria-label="Main navigation">
              <ul className="flex items-center gap-2">
                <li>
                  <a
                    href="/"
                    className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    Subdomains
                  </a>
                </li>
              </ul>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={logout}
              className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="Sign out"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
